import { CommonUtils } from "../util/common-utils";
import { VanillaSliderForm } from "./vanilla-slider-form";

type SwiperBreakpoints = Record<number, { slidesPerView: number | string; spaceBetween: number }>;

type SwiperInstance = {
    progress: number;
    isLocked: boolean;
    on: (event: string, cb: () => void) => void;
    destroy: (deleteInstance?: boolean, cleanStyles?: boolean) => void;
};

type SwiperConstructor = new (el: HTMLElement, opts: unknown) => SwiperInstance;

// Breakpoint presets per slider name — matches fe/ initSectionSlider usage
const BREAKPOINTS: Record<string, SwiperBreakpoints> = {
    products: {
        0: { slidesPerView: 2.0, spaceBetween: 12 },
        640: { slidesPerView: 2.83, spaceBetween: 12 },
        992: { slidesPerView: 3.83, spaceBetween: 12 },
        1200: { slidesPerView: 4.83, spaceBetween: 12 },
        1440: { slidesPerView: 5.83, spaceBetween: 12 },
    },
    users: {
        // Best 9 design: 3 full cards on mobile (no partial 4th peek), matching
        // desktop's 3x3-per-page intent at the narrowest breakpoint.
        0: { slidesPerView: 3, spaceBetween: 8 },
        640: { slidesPerView: 3.33, spaceBetween: 12 },
        992: { slidesPerView: 4.33, spaceBetween: 12 },
        1200: { slidesPerView: 5.33, spaceBetween: 12 },
        1440: { slidesPerView: 6.33, spaceBetween: 12 },
    },
    posts: {
        0: { slidesPerView: 1.7, spaceBetween: 8 },
        640: { slidesPerView: 2.33, spaceBetween: 8 },
        992: { slidesPerView: 3.33, spaceBetween: 8 },
        1200: { slidesPerView: 4.33, spaceBetween: 8 },
        1440: { slidesPerView: 5.33, spaceBetween: 8 },
    },
    // SNS Reviews carousel (brand + product detail pages) — sized per explicit
    // request (first tried 4+1/4, adjusted to 4+1/3, then found that a
    // devtools-narrowed viewport was hitting the 992/1200 tiers instead of
    // 1440 and showing far fewer cards than intended — so 992/1200/1440 all
    // show the same "4+1/3" now, i.e. a fixed slide count across the whole
    // desktop range instead of growing narrower at smaller desktop widths).
    // Kept as its own preset so it doesn't affect the other name="posts"
    // sliders (home's recommended-reviews carousel).
    sns_reviews: {
        0: { slidesPerView: 1.33, spaceBetween: 8 },
        640: { slidesPerView: 1.83, spaceBetween: 8 },
        992: { slidesPerView: 4.33, spaceBetween: 8 },
        1200: { slidesPerView: 4.33, spaceBetween: 8 },
        1440: { slidesPerView: 4.33, spaceBetween: 8 },
    },
    awards: {
        0: { slidesPerView: 1.7, spaceBetween: 8 },
        640: { slidesPerView: 2.25, spaceBetween: 20 },
        992: { slidesPerView: 3.25, spaceBetween: 24 },
        1200: { slidesPerView: 4.25, spaceBetween: 24 },
        1440: { slidesPerView: 5.25, spaceBetween: 24 },
    },
    // Home "New Reviews" carousel. Cards render as list_style=LIST
    // board-card--list — a two-column (text + 140px media tile) datagrid row that
    // needs ~450px+ or the text column is squeezed to a sliver. Keep the per-view
    // count low; the fractional part is the "peek" hint that there's more.
    review: {
        0: { slidesPerView: 1.05, spaceBetween: 16 },
        640: { slidesPerView: 1.4, spaceBetween: 20 },
        992: { slidesPerView: 2.3, spaceBetween: 24 },
        1200: { slidesPerView: 3, spaceBetween: 24 },
        1440: { slidesPerView: 3.5, spaceBetween: 24 },
    },
    profile: {
        0: { slidesPerView: 2.2, spaceBetween: 16 },
        640: { slidesPerView: 3.5, spaceBetween: 20 },
        992: { slidesPerView: 5, spaceBetween: 24 },
        1200: { slidesPerView: 6, spaceBetween: 24 },
        1440: { slidesPerView: 8, spaceBetween: 24 },
    },
    activity: {
        0: { slidesPerView: 1.12, spaceBetween: 16 },
        640: { slidesPerView: 2, spaceBetween: 20 },
        992: { slidesPerView: 3, spaceBetween: 24 },
        1200: { slidesPerView: 4, spaceBetween: 24 },
        1440: { slidesPerView: 6, spaceBetween: 24 },
    },
};

export class VanillaSliderHandler {
    private readonly form: VanillaSliderForm;

    private swiper: SwiperInstance | null = null;

    public constructor(form: VanillaSliderForm) {
        this.form = form;
    }

    public bootstrap(): void {
        if (!CommonUtils.isMobileViewport()) {
            this.initSwiper();
        }
    }

    public onMobileChange(isMobile: boolean): void {
        if (isMobile) {
            this.destroySwiper();
        } else {
            this.initSwiper();
        }
    }

    public initSwiper(): void {
        if (this.swiper || !this.form.swiperEl) return;

        const SwiperClass = (window as unknown as { Swiper?: SwiperConstructor }).Swiper;
        if (!SwiperClass) return;

        const breakpoints = BREAKPOINTS[this.form.sliderName] ?? BREAKPOINTS.products;
        this.swiper = new SwiperClass(this.form.swiperEl, {
            slidesPerView: 1.33,
            spaceBetween: 12,
            slidesPerGroup: 1,
            watchOverflow: true,
            resistanceRatio: 0,
            observer: true,
            observeParents: true,
            navigation: {
                prevEl: this.form.prevBtn,
                nextEl: this.form.nextBtn,
                disabledClass: "swiper-button-disabled",
            },
            breakpoints,
        });

        this.bindProgress(this.swiper);
    }

    public destroySwiper(): void {
        if (!this.swiper) return;
        this.swiper.destroy(true, true);
        this.swiper = null;
    }

    private bindProgress(swiper: SwiperInstance): void {
        const bar = this.form.progressBar;
        if (!bar) return;
        const update = (): void => {
            if (swiper.isLocked) {
                bar.style.width = "100%";
                return;
            }
            bar.style.width = Math.max(10, swiper.progress * 100) + "%";
        };
        swiper.on("progress", update);
        swiper.on("resize", update);
        swiper.on("slideChange", update);
        update();
    }
}
